import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import merge from 'lodash/merge';
import { eventChannel } from 'redux-saga';
import { call, cancel, select, take } from 'redux-saga/effects';

import { getNow } from '../../lib/TimeUtils';
import * as StoreUtils from '../../store/StoreAccessor';
import Logger from '../../tools/Logger';

import { taskManagerSpawnFactory } from './SpawnedTaskManager';

const log = Logger.child({ component: 'sagas' });
const spawn = taskManagerSpawnFactory();
const LOOP = true;

export function* wrap(saga, ...args) {
  try {
    return yield call(saga, ...args);
  } catch (err) {
    log.error(err, `${saga.name}(): ${err.message}`, ...args);
  }
}

export function* wrapAndYieldError(saga, ...args) {
  let result;
  let error;
  try {
    result = yield call(saga, ...args);
  } catch (err) {
    log.error(err, saga.name);
    error = err;
  }
  return yield { result, error };
}

/**
 * Adapter function for allowing Saga to call thunks
 *
 * @param  {Function} fn   the thunk to call
 * @param  {Array}   args arguments to pass to thunk
 * @returns {Promise}        the thunk resolution
 */
export function thunkify(fn, ...args) {
  const { dispatch, getState } = StoreUtils.getStore();
  return fn(...args)(dispatch, getState);
}

function actionPassThrough(func) {
  return function* (action) {
    const res = yield call(func, action);
    if (isObject(res)) {
      return merge(action, res);
    }
    return action;
  };
}

/**
 * calls multiple sagas in sequence
 *
 * @example
 *   const tasks = pipe([
 *     _.partial(saga1, 'foo'),
 *     _.partial(saga2, 'bar')
 *   ]);
 *   yield call(tasks);
 * @param  {Array} sagas - sagas to sequentially call
 * @returns {Function} - sequential generator
 */
export function pipe(sagas) {
  if (!isArray(sagas)) {
    throw new Error('pipe(): parameter sagas must be an array');
  }

  return function* (initialValue) {
    let accumulatedValue = initialValue;
    for (const saga of sagas) {
      accumulatedValue = yield call(actionPassThrough(saga), accumulatedValue);
    }
    return accumulatedValue;
  };
}

export function cond(pairs) {
  return function* (...args) {
    for (const [test, response] of pairs) {
      const isMatch = yield call(test, ...args);
      if (isMatch) {
        return yield call(response, ...args);
      }
    }
  };
}

/**
 * Accepts a test function and two saga functions.
 *
 * @example
 *   yield branch(test, saga1, saga2)('foo', 'bar');
 *   yield call(branch(test, saga1, saga2), 'foo', 'bar');
 * @param  {Function} test - test function
 * @param  {Function} left - saga to call if test returns true
 * @param  {Function} [right] - saga to call if test returns false
 * @returns {Function} - either the left or the right saga call with the supplied args
 */
export function branch(test, left, right) {
  return function* (...args) {
    const isMatch = yield call(test, ...args);
    if (isMatch) {
      return yield call(left, ...args);
    } else if (right) {
      return yield call(right, ...args);
    }
  };
}

// wrap the call effect
export const tryCall = (saga, ...args) => call(wrap, saga, ...args);
// wrap and yield the error if there is one
export const tryCallYield = (saga, ...args) => call(wrapAndYieldError, saga, ...args);

export function* takeLatestWithSpawn(pattern, saga, ...args) {
  let lastTask;
  while (LOOP) {
    const action = yield take(pattern);
    if (lastTask) {
      yield cancel(lastTask); // cancel is no-op if the task has already terminated
    }
    lastTask = yield spawn(saga, ...args.concat(action));
    lastTask.toPromise().catch(err => {
      log.error(err, saga.name);
    });
  }
}

// a function that returns a function that will ensure that the looping isn't occuring too
// rapidly, kind of like a circuit breaker. Example:
//
// const loop = getBreakerLooper();
// while (loop()) {
//  doStuff
// }
//
// if the thresholds are reached then the loop function will return false
// and the loop using the looper should exit and error handling can be done at that point
export function getBreakerLoop({ thresholdMS = 10, maxAllowedInThreshold = 10 } = {}) {
  let before = getNow();
  let countInThreshold = 0;
  let isInThreshold;
  return () => {
    const after = getNow();
    if (after - before < thresholdMS) {
      countInThreshold++;
    } else {
      countInThreshold = 0;
    }
    isInThreshold = countInThreshold < maxAllowedInThreshold;
    before = after;
    return isInThreshold;
  };
}

export function* getOrFetch({ selector, selectorArgs = [], fetcher, force = false }) {
  const result = yield select(selector, ...selectorArgs);
  if (
    (!isNil(result) && !force && isNil(result.size)) ||
    (!force && result && result.size && result.size > 0)
  ) {
    return result;
  }
  yield call(fetcher);
  return yield select(selector, ...selectorArgs);
}

/**
 * Attaches event listeners to window (or specified target) from a saga and emits event objects to channel.
 * These event listeners will only be removed if saga calls `channel.close()`.
 *
 * @param  {(string|string[])} events - Array of DOM events to listen for on target, can be a string if single event
 * @param  {object} target - target to listen on, defaults to window
 * @param  {(boolean | object)} useCapture - see similar param in browser addEventListener() documentation
 * @returns {object} - event channel
 */
// eslint-disable-next-line require-yield
export function* createDOMEventChannel(events, target = window, useCapture = false) {
  return eventChannel(emitter => {
    const handler = event => {
      emitter(event);
    };

    const listenEvents = isArray(events) ? events : [events];

    log.info(`createDOMEventChannel(): starting DOM event channel [${listenEvents}]`);
    listenEvents.forEach(event => {
      target.addEventListener(event, handler, useCapture);
    });

    const removeEventListener = () => {
      log.info(`createDOMEventChannel(): closing DOM event channel [${listenEvents}]`);
      listenEvents.forEach(event => {
        target.removeEventListener(event, handler, useCapture);
      });
    };

    return removeEventListener;
  });
}
